import type { CountryRule } from '@/types/platform';

/**
 * Country-aware address rules — BUILD-PLAN §24, §41, §43.
 *
 * Address shape is not universal, and pretending otherwise produces forms that
 * are wrong nearly everywhere. "ZIP code" means nothing in the UK; Ireland's
 * Eircode is genuinely optional; the UAE has no postal codes at all; a US
 * address without a state is undeliverable while a French one never has a
 * region. A single "Postcode *" field applied to every country either rejects
 * valid addresses or accepts undeliverable ones.
 *
 * The rules themselves now live in `config/countries.php` and arrive through
 * /bootstrap. They used to be maintained here as well, which meant two lists
 * that could disagree — and a form that disagrees with the API is a form that
 * rejects valid addresses for reasons the customer cannot see. The server is
 * the authority because the server is what validates.
 */

export type { CountryRule };

/** A resolved rule with the pattern compiled and defaults applied. */
export interface ResolvedCountryRule {
  code: string;
  name: string;
  currency: string;
  dialCode: string | null;

  postcodeLabel: string | null;
  postcodeRequired: boolean;
  postcodePattern: RegExp | null;
  postcodeExample: string | null;
  uppercasePostcode: boolean;

  regionLabel: string | null;
  regionRequired: boolean;
  regionOptions: Array<{ value: string; label: string }> | null;
}

/**
 * A market we know nothing about.
 *
 * Permissive on purpose: an unlisted country is a gap in the registry, and
 * blocking the customer is a worse answer than accepting an address we cannot
 * pre-check. The server still validates.
 */
const UNKNOWN: ResolvedCountryRule = {
  code: '',
  name: '',
  currency: 'GBP',
  dialCode: null,
  postcodeLabel: 'Postal code',
  postcodeRequired: false,
  postcodePattern: null,
  postcodeExample: null,
  uppercasePostcode: false,
  regionLabel: 'Region',
  regionRequired: false,
  regionOptions: null,
};

/*
 * Compiled patterns are memoised. `rulesFor` is called on every render of the
 * checkout form, and rebuilding a RegExp each time is waste that shows up as
 * jank while typing.
 */
const patternCache = new Map<string, RegExp | null>();

function compile(pattern: string | undefined, key: string): RegExp | null {
  if (!pattern) return null;

  if (patternCache.has(key)) return patternCache.get(key) ?? null;

  let compiled: RegExp | null = null;

  try {
    compiled = new RegExp(pattern, 'i');
  } catch {
    /*
     * A malformed pattern from the server must not take the checkout down.
     * Treating it as "no format check" leaves the field usable and lets the
     * server have the final word, which it does anyway.
     */
    compiled = null;
  }

  patternCache.set(key, compiled);

  return compiled;
}

export function resolveCountry(country: CountryRule): ResolvedCountryRule {
  return {
    code: country.code,
    name: country.name,
    currency: country.currency,
    dialCode: country.dialCode,

    postcodeLabel: country.postcode?.label ?? null,
    postcodeRequired: country.postcode?.required ?? false,
    postcodePattern: compile(country.postcode?.pattern, country.code),
    postcodeExample: country.postcode?.example ?? null,
    uppercasePostcode: country.postcode?.uppercase ?? false,

    regionLabel: country.region?.label ?? null,
    regionRequired: country.region?.required ?? false,
    regionOptions: country.region?.options ?? null,
  };
}

/**
 * Rules for a country code, from the registry the server published.
 *
 * Falls back to a permissive shape rather than throwing: a country missing
 * from the registry is our gap, not the customer's mistake.
 */
export function rulesFor(
  countryCode: string,
  countries: CountryRule[] = [],
): ResolvedCountryRule {
  const code = countryCode.toUpperCase();
  const match = countries.find((country) => country.code.toUpperCase() === code);

  return match ? resolveCountry(match) : { ...UNKNOWN, code };
}
